import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { ASSISTENT_API_URL } from '@/config/backend'
import { useToast } from '@/hooks/use-toast'
import { useAuthStore } from '@/stores/auth-store'
import { useDevicesStore } from '@/stores/devices-store'
import axios from 'axios'
import { AlertCircle, Check, Loader2, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface ConfigField {
    key: string
    label: string
    type: 'text' | 'password' | 'url' | 'boolean'
    valueLabel?: string
    required?: boolean
}

// Configuration fields per API type – keys MUST match old frontend's apiTypes.js
// These are the actual JSON keys stored in RESTAURANT_CONFIG on the backend
const API_TYPE_CONFIGS: Record<string, ConfigField[]> = {
    GOOGLE: [
        { key: 'calendarId', label: 'Kalender ID', type: 'text', required: true },
    ],
    RESTAURANT_ALENO: [
        { key: 'alenoId', label: 'Restaurant ID', type: 'text', required: true },
        { key: 'apiUser', label: 'API Key', type: 'text', required: true },
        { key: 'apiPasswordHash', label: 'API Passwort', type: 'password', required: true },
    ],
    RESTAURANT_BANKETTPROFI: [
        { key: 'availabilityApiUrl', label: 'Verfügbarkeits-URL', type: 'url', required: true },
        { key: 'reservationApiUrl', label: 'Buchungs-URL', type: 'url', required: true },
    ],
    RESTAURANT_FORATABLE: [
        { key: 'restaurantId', label: 'Restaurant ID', type: 'text', required: true },
        { key: 'username', label: 'API Key', type: 'text', required: true },
        { key: 'password', label: 'API Passwort', type: 'password', required: true },
    ],
    RESTAURANT_FORMITABLE: [
        { key: 'restaurantId', label: 'Restaurant ID', type: 'text', required: true },
        { key: 'apiKey', label: 'API Key', type: 'password', required: true },
    ],
    RESTAURANT_GASTRODIGITAL: [
        { key: 'apiKey', label: 'API Key', type: 'text', required: true },
    ],
    RESTAURANT_RESERVINO: [
        { key: 'restaurantId', label: 'Restaurant ID', type: 'text', required: true },
        { key: 'transferCallerNumberToReservino', label: 'Mobilnummer übertragen', type: 'boolean', valueLabel: 'Anrufer-Nummer an Reservino übertragen' },
    ],
    RESTAURANT_RESERVISION: [
        { key: 'username', label: 'API Key', type: 'text', required: true },
        { key: 'password', label: 'API Passwort', type: 'password', required: true },
    ],
    RESTAURANT_RESMIO: [
        { key: 'restaurantId', label: 'Restaurant ID', type: 'text', required: true },
        { key: 'apiKey', label: 'API Key', type: 'password', required: true },
        { key: 'promoBookable', label: 'Promo buchbar', type: 'boolean', valueLabel: 'Promo-Buchungen aktivieren' },
    ],
    RESTAURANT_TABLEX: [
        { key: 'restaurantId', label: 'Restaurant ID', type: 'text', required: true },
        { key: 'restaurantUrl', label: 'API URL', type: 'url', required: true },
    ],
    RESTAURANT_TABLY: [
        { key: 'restaurantId', label: 'Restaurant ID', type: 'text', required: true },
        { key: 'apiKey', label: 'API Passwort', type: 'password', required: true },
    ],
    RESTAURANT_TEBURIO: [
        { key: 'locationId', label: 'Location ID', type: 'text', required: true },
        { key: 'authToken', label: 'Auth Token', type: 'password', required: true },
    ],
    RESTAURANT_ZENCHEF: [
        { key: 'restaurantId', label: 'Restaurant ID', type: 'text', required: true },
        { key: 'apiKey', label: 'API Passwort', type: 'password', required: true },
    ],
}

export function IntegrationConfig() {
    const { t } = useTranslation()
    const { currentDevice, updateConfigData, getData } = useDevicesStore()
    const { canEdit, getApiHeaders } = useAuthStore()
    const { toast } = useToast()
    const [isLoading, setIsLoading] = useState(false)
    const [isValidating, setIsValidating] = useState(false)
    const [validationStatus, setValidationStatus] = useState<'success' | 'error' | null>(null)
    const [apiType, setApiType] = useState<string>('')
    const [configFields, setConfigFields] = useState<ConfigField[]>([])
    const [formValues, setFormValues] = useState<Record<string, string | boolean>>({})
    const [isDirty, setIsDirty] = useState(false)

    useEffect(() => {
        if (currentDevice?.deviceId) {
            loadData()
        }
    }, [currentDevice?.deviceId])

    const loadData = () => {
        if (!currentDevice || !currentDevice.deviceConfiguration) {
            return
        }

        // Get API type from device config
        const calendarApi = String(getData('CALENDAR_API') || '')
        setApiType(calendarApi)

        // Get config fields for this API type
        const fields = API_TYPE_CONFIGS[calendarApi] || []
        setConfigFields(fields)

        // Load RESTAURANT_CONFIG JSON and parse values
        const restaurantConfigStr = String(getData('RESTAURANT_CONFIG') || '{}')
        try {
            const restaurantConfig = JSON.parse(restaurantConfigStr)
            const values: Record<string, string | boolean> = {}

            fields.forEach(field => {
                if (field.type === 'boolean') {
                    values[field.key] = restaurantConfig[field.key] === 'TRUE' || restaurantConfig[field.key] === 'true'
                } else {
                    values[field.key] = restaurantConfig[field.key] || ''
                }
            })

            setFormValues(values)
        } catch (e) {
            console.error('Failed to parse RESTAURANT_CONFIG:', e)
            setFormValues({})
        }

        setIsDirty(false)

        // Validate API credentials on load
        validateCredentials()
    }

    const validateCredentials = async () => {
        // Skip validation for certain API types
        if (apiType === 'GOOGLE' || apiType === 'RESTAURANT_BANKETTPROFI' || !apiType) {
            setValidationStatus(null)
            return
        }

        setIsValidating(true)
        try {
            const url = `${ASSISTENT_API_URL}configuration/api/check/${currentDevice?.deviceId}`
            const response = await axios.post(url, null, {
                headers: getApiHeaders()
            })
            setValidationStatus(response.data.resultObject ? 'success' : 'error')
        } catch (error) {
            console.error('API validation failed:', error)
            setValidationStatus('error')
        } finally {
            setIsValidating(false)
        }
    }

    const handleFieldChange = (key: string, value: string | boolean) => {
        setFormValues(prev => ({ ...prev, [key]: value }))
        setIsDirty(true)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!isDirty) return

        setIsLoading(true)

        try {
            // Build the config object
            const restaurantConfigStr = String(getData('RESTAURANT_CONFIG') || '{}')
            const config = JSON.parse(restaurantConfigStr)

            // Update with form values
            configFields.forEach(field => {
                if (field.type === 'boolean') {
                    config[field.key] = formValues[field.key] ? 'TRUE' : 'FALSE'
                } else {
                    config[field.key] = formValues[field.key] || ''
                }
            })

            // Save the config
            await updateConfigData('RESTAURANT_CONFIG', JSON.stringify(config))

            toast({
                title: t('success.title'),
                description: t('integrationConfig.saveSuccess'),
            })

            setIsDirty(false)

            // Re-validate after save
            await validateCredentials()
        } catch (error) {
            console.error('Error saving integration config:', error)
            toast({
                variant: 'destructive',
                title: t('error.title'),
                description: t('integrationConfig.saveError'),
            })
        } finally {
            setIsLoading(false)
        }
    }

    const isDisabled = !canEdit() || isLoading

    if (!apiType) {
        return (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                    <strong>{t('integrationConfig.notice')}:</strong> {t('integrationConfig.noSystem')}
                </p>
            </div>
        )
    }

    return (
        <div className="space-y-6">
            {/* API Type Header */}
            <div className="pb-4 border-b">
                <h3 className="text-lg font-semibold">{t('integrationConfig.configFor')} {apiType.replace('RESTAURANT_', '').replace('_', ' ')}</h3>
            </div>

            {/* Validation Status */}
            {isValidating ? (
                <div className="rounded-lg p-4 bg-gray-50 border border-gray-200">
                    <div className="flex items-center">
                        <Loader2 className="w-5 h-5 text-gray-500 mr-3 animate-spin" />
                        <p className="text-gray-600 font-medium">{t('integrationConfig.validating')}</p>
                    </div>
                </div>
            ) : validationStatus && (
                <div
                    className={`rounded-lg p-4 ${validationStatus === 'success'
                        ? 'bg-green-50 border border-green-200'
                        : 'bg-red-50 border border-red-200'
                        }`}
                >
                    <div className="flex items-center">
                        {validationStatus === 'success' ? (
                            <>
                                <Check className="w-5 h-5 text-green-500 mr-3" />
                                <p className="text-green-800 font-medium">
                                    {t('configuration.device.api.validation.credentialsOk')}
                                </p>
                            </>
                        ) : (
                            <>
                                <X className="w-5 h-5 text-red-500 mr-3" />
                                <p className="text-red-800 font-medium">
                                    {t('configuration.device.api.validation.credentialsError')}
                                </p>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Configuration Form */}
            {configFields.length > 0 ? (
                <form onSubmit={handleSubmit} className="space-y-6">
                    {configFields.map((field) => (
                        <div key={field.key} className="space-y-2">
                            {field.type === 'boolean' ? (
                                <div className="flex items-center justify-between rounded-lg border p-4">
                                    <div className="space-y-0.5">
                                        <Label htmlFor={field.key} className="text-sm font-medium">
                                            {field.label}
                                        </Label>
                                        <p className="text-sm text-gray-500">
                                            {field.valueLabel || t('integrationConfig.enableDisable')}
                                        </p>
                                    </div>
                                    <Switch
                                        id={field.key}
                                        checked={formValues[field.key] as boolean || false}
                                        onCheckedChange={(checked) => handleFieldChange(field.key, checked)}
                                        disabled={isDisabled}
                                    />
                                </div>
                            ) : (
                                <>
                                    <Label htmlFor={field.key} className="text-sm font-semibold text-gray-700">
                                        {field.label}
                                        {field.required && <span className="text-red-500 ml-1">*</span>}
                                    </Label>
                                    <Input
                                        id={field.key}
                                        type={field.type === 'url' ? 'text' : field.type}
                                        value={formValues[field.key] as string || ''}
                                        onChange={(e) => handleFieldChange(field.key, e.target.value)}
                                        placeholder={
                                            field.type === 'url'
                                                ? 'https://api.example.com'
                                                : field.type === 'password'
                                                    ? '••••••••'
                                                    : `${field.label}...`
                                        }
                                        disabled={isDisabled}
                                    />
                                </>
                            )}
                        </div>
                    ))}

                    {/* Submit Button */}
                    <div className="pt-6 border-t border-gray-200">
                        <Button
                            type="submit"
                            disabled={!isDirty || isDisabled}
                            className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primary/90"
                        >
                            {isLoading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Check className="w-4 h-4" />
                            )}
                            {isLoading ? t('global.button.loading') : t('global.button.saveChanges')}
                        </Button>
                    </div>
                </form>
            ) : (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                    <p className="text-sm text-red-800">
                        {t('integrationConfig.noConfig')}
                    </p>
                </div>
            )}
        </div>
    )
}
